import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { InputField } from '@/components/InputField';
import { SwitchField } from '@/components/SwitchField/SwitchField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { PositionOrderField } from '../PositionOrderField';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePostRosterGroupMutation } from '@/services/queries/roster-group';
import { PostRosterGroupRequest } from '@/services/request/roster-group';

export interface RosterGroupCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const RosterGroupCreateModal = ({ isOpen, onClose, onDone }: RosterGroupCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostRosterGroupMutation();

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      name: '',
      show_stats: false,
      position_order: [],
      sort_order: 0,
      team_uuid: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostRosterGroupRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        name: data.name,
        show_stats: data.show_stats,
        position_order: data.position_order,
        sort_order: data.sort_order,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_team')}
              placeholder={t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_name')}
              {...register('name')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
            />

            <InputField
              withoutFloating
              type='number'
              label={t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_sort_order')}
              {...register('sort_order')}
              rootProps={{ isInvalid: !!errors?.sort_order, errorMessage: errors?.sort_order?.message }}
            />

            <SwitchField
              label={t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_show_stats')}
              isChecked={watch('show_stats')}
              onChange={event => setValue('show_stats', event.target.checked)}
            />

            <PositionOrderField
              value={watch('position_order') ?? []}
              onChange={next => setValue('position_order', next)}
            />
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_cancel')}
            </Button>
            <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
              {t('pages.application_roster_group.components.roster_group_create_modal.roster_group_create_create')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
